'use client'

import { useState, useEffect } from 'react'
import { List, X } from 'phosphor-react'
import { Sparkles } from 'lucide-react'
import { useAssistant } from '@/lib/assistantContext'

interface NavbarComponentProps {
  theme: any;
  logoUrl: string;
  radius: number;
  availableSections?: string[];
}

export default function NavbarComponent({ 
  theme, 
  logoUrl, 
  radius, 
  availableSections = []
}: NavbarComponentProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { openPanel } = useAssistant();
  
  // Generate navigation items based on available sections only (no duplicates)
  const allNavItems = availableSections.map(section => {
    const sectionLabels: Record<string, string> = {
      'audience': 'Our Audience',
      'inventory': 'Advertise',
      'contactfaq': 'Contact',
      'aboutus': 'About'
    };
    
    return {
      id: section,
      label: sectionLabels[section] || section,
      href: `#${section}`
    };
  }).filter(item => item.label !== item.id);

  // Body scroll lock when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Handle smooth scrolling with offset for fixed navbar
  const handleSmoothScroll = (href: string) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        const navbarHeight = 80;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navbarHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      window.location.href = href;
    }
    setIsMobileMenuOpen(false);
  };

  const getNavbarBackground = () => {
    return {
      background: '#103065',
      borderBottom: "none"
    };
  };

  return (
    <>
      <nav 
        className="w-full z-50 fixed top-0 left-0 right-0"
        style={{
          ...getNavbarBackground(),
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)'
        }}
      >
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              {logoUrl ? (
                <img 
                  src={`${logoUrl}?v=${Date.now()}`} 
                  alt="Windy City Media Group Logo" 
                  className="h-16 w-auto"
                  style={{ borderRadius: radius / 2 }}
                />
              ) : (
                <div 
                  className="h-12 w-32 flex items-center justify-center text-lg font-bold font-primary"
                  style={{ 
                    color: theme.text,
                    backgroundColor: theme.card,
                    borderRadius: radius / 2
                  }}
                >
                  Logo
                </div>
              )}
            </div>

            {/* Desktop Navigation + CTA */}
            <div className="hidden md:flex items-center space-x-8">
              {/* Navigation Items */}
              <div className="flex items-center space-x-6">
                {allNavItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSmoothScroll(item.href)}
                    className="text-sm font-medium transition-all duration-200 hover:opacity-80 font-primary relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-white after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left"
                    style={{ color: '#FFFFFF' }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* CTA Button with Sparkles Icon */}
              <button
                onClick={openPanel}
                className="px-5 py-2.5 text-sm font-semibold transition-all duration-200 hover:shadow-lg hover:scale-105 font-primary flex items-center"
                style={{
                  backgroundColor: theme.accent,
                  color: theme.onButton,
                  borderRadius: radius
                }}
              >
                <Sparkles className="w-4 h-4 mr-2 animate-sparkle-twinkle" />
                Assistant
              </button>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 transition-colors duration-200"
              aria-label="Open menu"
            >
              <List size={28} color="#FFFFFF" weight="bold" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[100] md:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-72 transform transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ background: '#103065' }}
        >
          {/* Close Button */}
          <div className="flex justify-end p-6">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 transition-colors duration-200 hover:opacity-80"
              aria-label="Close menu"
            >
              <X size={28} color="#FFFFFF" weight="bold" />
            </button>
          </div>

          {/* Navigation Links */}
          <div className="flex flex-col px-6 space-y-2">
            {allNavItems.map((item, index) => (
              <button
                key={item.id}
                onClick={() => handleSmoothScroll(item.href)}
                className="text-left py-4 text-lg font-medium transition-all duration-200 hover:opacity-80 hover:translate-x-2 font-primary border-b border-white/10"
                style={{ 
                  color: '#FFFFFF',
                  animationDelay: `${index * 50}ms`
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* CTA Button */}
          <div className="px-6 mt-8">
            <button
              onClick={() => {
                openPanel();
                setIsMobileMenuOpen(false);
              }}
              className="w-full px-5 py-3 text-base font-semibold transition-all duration-200 hover:shadow-lg font-primary flex items-center justify-center gap-2"
              style={{
                backgroundColor: theme.accent,
                color: theme.onButton,
                borderRadius: radius
              }}
            >
              <Sparkles className="w-5 h-5 animate-sparkle-twinkle" />
              Assistant
            </button>
          </div>
        </div>
      </div>
    </>
  );
}